import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { UploadCloud, FileText, Image as ImageIcon, File as FileIcon, X, Mail, Users } from 'lucide-react'
import { api, ApiError, kycApi, type Me } from '../lib/api'
import { SearchableSelect } from '../components/SearchableSelect'
import { RichText } from '../components/RichText'
import { useToast } from '../components/Toast'

function fileGlyph(f: File) {
  if (f.type.startsWith('image/')) return <ImageIcon size={18} />
  if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) return <FileText size={18} />
  if (/word|document|\.docx?$/i.test(f.type + f.name)) return <FileText size={18} />
  if (/text|\.txt$/i.test(f.type + f.name)) return <FileText size={18} />
  return <FileIcon size={18} />
}

/**
 * Compose and send a message from the dashboard.
 *
 * The API is the real integration surface, but a send box in the UI is the fastest way to
 * answer the only question that matters right after setup: does mail from my verified
 * domain actually arrive? So this is deliberately minimal — pick a verified domain, type
 * an address, send, and watch it in the message log.
 */
function fileToB64(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader()
    r.onload = () => res((r.result as string).split(',')[1] ?? '')
    r.onerror = () => rej(r.error)
    r.readAsDataURL(f)
  })
}
/** A bulk recipient: one per line, "email", "email, Name", "Name, email" or "Name <email>". */
interface BulkRecipient { email: string; name: string }
const EMAIL_RE = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[a-z]{2,}$/i
function parseRecipients(raw: string): { list: BulkRecipient[]; invalid: string[]; duplicates: number } {
  const seen = new Set<string>(); const list: BulkRecipient[] = []; const invalid: string[] = []; let duplicates = 0
  let first = true
  for (const line of raw.split(/\r?\n/)) {
    const l = line.trim(); if (!l) continue
    const isFirst = first; first = false
    let email = '', name = ''
    const angle = l.match(/^"?([^"<]*)"?\s*<([^>]+)>/)
    if (angle) { name = (angle[1] ?? '').trim(); email = (angle[2] ?? '').trim() }
    else {
      const cells = l.split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, ''))
      const i = cells.findIndex(c => EMAIL_RE.test(c))
      if (i < 0) { if (!(isFirst && /e-?mail/i.test(l))) invalid.push(l); continue }   // a CSV header row is skipped quietly
      email = cells[i] ?? ''; name = cells.find((c, j) => j !== i && c && !EMAIL_RE.test(c)) ?? ''
    }
    if (!EMAIL_RE.test(email)) { invalid.push(l); continue }
    const key = email.toLowerCase()
    if (seen.has(key)) { duplicates++; continue }
    seen.add(key); list.push({ email, name })
  }
  return { list, invalid, duplicates }
}
const PERSONAL = ['name', 'first_name', 'email']
function personalize(s: string, r: BulkRecipient, html: boolean) {
  const esc = (v: string) => html ? v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') : v
  const first = r.name.split(/\s+/)[0] ?? ''
  return s.replace(/\{\{\s*(name|first_name|email)\s*\}\}/gi, (_m, k: string) => {
    const key = k.toLowerCase()
    return esc(key === 'email' ? r.email : key === 'first_name' ? (first || 'there') : (r.name || 'there'))
  })
}

function fmtBytes(n: number) {
  if (!n) return '0 B'
  const u = ['B', 'KB', 'MB', 'GB']; const i = Math.floor(Math.log(n) / Math.log(1024))
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`
}

export function Send({ me }: { me: Me }) {
  const toast = useToast()
  const { data: domains } = useQuery({ queryKey: ['domains'], queryFn: api.domains.list })
  const { data: tplList } = useQuery({ queryKey: ['templates'], queryFn: () => api.templates.list() })
  const templates = tplList?.data ?? []
  const [templateId, setTemplateId] = useState('')
  const [vars, setVars] = useState<Record<string, string>>({})

  const sendable = (domains?.data ?? []).filter(d => d.can_send)
  const [local, setLocal] = useState('hello')
  const [fromName, setFromName] = useState('')
  const [domain, setDomain] = useState('')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [showCcBcc, setShowCcBcc] = useState(false)
  const [subject, setSubject] = useState('Test from ItsBingMyBusiness')
  const [text, setText] = useState('This is a test message sent from the ItsBingMyBusiness dashboard.')
  const [mode, setMode] = useState<'text' | 'html'>('text')
  const [source, setSource] = useState(false)   // HTML mode: design (false) vs raw source (true)
  const [files, setFiles] = useState<File[]>([])
  const [dragOver, setDragOver] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return
    // Capture the File objects NOW — the state updater runs async, by which time the input's
    // value has been reset and its FileList is empty. That was why uploads didn't appear.
    const arr = Array.from(list)
    setFiles(prev => [...prev, ...arr])
  }
  const [kind, setKind] = useState<'single' | 'bulk'>('single')
  const [bulkRaw, setBulkRaw] = useState('')
  const [marketing, setMarketing] = useState(true)
  const [consent, setConsent] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [bulkResult, setBulkResult] = useState<{ queued: number; failed: number; errors: string[] } | null>(null)
  const csvInput = useRef<HTMLInputElement>(null)
  const parsed = parseRecipients(bulkRaw)
  // Today's marketing allowance (verified customer accounts only; operators have none).
  const { data: limits, refetch: refetchLimits } = useQuery({
    queryKey: ['account-limits'], queryFn: kycApi.limits, enabled: kind === 'bulk' && !!me.kyc,
  })
  const marketingLeft = limits?.marketing_remaining_today
  const overAllowance = marketing && marketingLeft != null && parsed.list.length > marketingLeft
  const [result, setResult] = useState<{ id: string; recipients: number } | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  const activeDomain = domain || sendable[0]?.name || ''
  const email = activeDomain ? `${local}@${activeDomain}` : ''
  // If a display name is given, send "Name <email>" — the API parses it into from_name + address.
  const from = fromName.trim() && email ? `${fromName.trim()} <${email}>` : email

  // Any {{placeholder}} tokens in the subject or body become fill-in fields below.
  const placeholders = Array.from(
    new Set([...`${subject} ${text}`.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map(m => m[1]).filter((k): k is string => !!k)))
    // In bulk mode the per-recipient fields and the unsubscribe link are filled automatically.
    .filter(k => kind === 'single' || ![...PERSONAL, 'unsubscribe_url'].includes(k.toLowerCase()))
  const fill = (s: string) => s.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, k) => vars[k] ?? `{{${k}}}`)

  const send = useMutation({
    mutationFn: async () => {
      const attachments = await Promise.all(files.map(async f => ({
        filename: f.name, content_type: f.type || undefined, content: await fileToB64(f),
      })))
      const ccList = cc.split(/[,\s]+/).filter(Boolean)
      const bccList = bcc.split(/[,\s]+/).filter(Boolean)
      return api.send.message({
        from,
        to: to.split(/[,\s]+/).filter(Boolean),
        cc: ccList.length ? ccList : undefined,
        bcc: bccList.length ? bccList : undefined,
        subject: fill(subject),
        text: mode === 'text' ? fill(text) : undefined,
        html: mode === 'html' ? fill(text) : undefined,
        attachments: attachments.length ? attachments : undefined,
      })
    },
    onSuccess: (r) => {
      setResult(r); setError(null); setFiles([])
      toast.success(`Message sent — queued to ${r.recipients} recipient${r.recipients === 1 ? '' : 's'}.`)
    },
    onError: (e) => {
      const err = e as ApiError
      setError(err)
      toast.error(err.message || 'Could not send the message.')
    },
  })

  // Bulk: every recipient gets their own copy (nobody sees the others), sent in batches of 500.
  const bulkSend = useMutation({
    mutationFn: async () => {
      const list = parsed.list; const CHUNK = 500
      let queued = 0, failed = 0; const errors: string[] = []
      setProgress({ done: 0, total: list.length })
      for (let i = 0; i < list.length; i += CHUNK) {
        const part = list.slice(i, i + CHUNK)
        try {
          const r = await api.send.batch({
            from, bulk: marketing,
            messages: part.map(rc => ({
              to: [rc.email],
              subject: personalize(fill(subject), rc, false),
              text: mode === 'text' ? personalize(fill(text), rc, false) : undefined,
              html: mode === 'html' ? personalize(fill(text), rc, true) : undefined,
              tags: ['dashboard-bulk'],
            })),
          })
          queued += r.queued
          for (const x of r.results) {
            if (x.status === 'queued') continue
            failed++
            if (errors.length < 8) errors.push(`${part[x.index]?.email}: ${x.status === 'suppressed' ? 'unsubscribed or bounced before — skipped' : x.error?.message ?? 'failed'}`)
          }
        } catch (e) {
          // A whole-batch refusal (limit reached, account state) stops the run here.
          errors.push((e as ApiError).message); failed += list.length - i
          break
        } finally {
          setProgress({ done: Math.min(i + CHUNK, list.length), total: list.length })
        }
      }
      return { queued, failed, errors }
    },
    onSuccess: (r) => {
      setBulkResult(r); refetchLimits()
      if (r.queued > 0) toast.success(`${r.queued} email${r.queued === 1 ? '' : 's'} queued, one per recipient.`)
      else toast.error(r.errors[0] ?? 'Nothing was sent.')
    },
    onError: (e) => toast.error((e as ApiError).message || 'Bulk send failed.'),
  })

  function tryBulk() {
    if (parsed.list.length === 0) { toast.warn('Add at least one recipient.'); return }
    if (!text.trim()) { toast.warn(`${mode === 'html' ? 'HTML body' : 'Message'} can’t be empty.`); return }
    if (marketing && !consent) { toast.warn('Confirm that these recipients agreed to hear from you.'); return }
    if (overAllowance) { toast.warn(`Only ${marketingLeft} marketing emails left today — shorten the list or raise the marketing share on Sending limits.`); return }
    if (!confirm(`Send ${parsed.list.length} separate email${parsed.list.length === 1 ? '' : 's'} from ${from}?`)) return
    setBulkResult(null); bulkSend.mutate()
  }

  async function loadCsv(f: File | undefined) {
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { toast.error('That file is over 5 MB.'); return }
    const t = await f.text()
    setBulkRaw(prev => (prev.trim() ? prev.trimEnd() + '\n' : '') + t)
    toast.info(`Loaded ${f.name}.`)
  }

  // Client-side checks with toast feedback before hitting the API.
  function trySend() {
    if (!to.trim()) { toast.warn('Add at least one recipient.'); return }
    if (!text.trim()) { toast.warn(`${mode === 'html' ? 'HTML body' : 'Message'} can’t be empty.`); return }
    send.mutate()
  }

  // Load a template's content into the compose fields so it can be reviewed and tweaked.
  async function pickTemplate(id: string) {
    setTemplateId(id)
    if (!id) return
    try {
      const t = await api.templates.get(id)
      if (t.subject) setSubject(t.subject)
      if (t.html) { setMode('html'); setSource(false); setText(t.html) }
      else if (t.text) { setMode('text'); setText(t.text) }
      toast.info(`Loaded template “${t.name}”. Edit and send.`)
    } catch { toast.error('Could not load that template.') }
  }

  const sandbox = me.tenant.is_sandboxed || me.tenant.status === 'pending_review'

  return (
    <div>
      <h1>Send a message</h1>
      <p className="lede">Send one email to a few people, or the same email to a whole list — from your verified domain.</p>

      {sendable.length === 0 ? (
        <div className="alert warn">
          <div>
            <strong>No verified domain yet.</strong> You can only send from a domain that
            has passed verification. <Link to="/domains">Finish DNS setup</Link> first.
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="send-kind" role="radiogroup" aria-label="Sending type">
            <button type="button" role="radio" aria-checked={kind === 'single'} className={kind === 'single' ? 'active' : ''} onClick={() => setKind('single')}>
              <span className="sk-ico"><Mail size={18} /></span>
              <span><strong>Individual email</strong><small>One message to one or a few people. Everyone on To and Cc sees each other. Attachments allowed.</small></span>
            </button>
            <button type="button" role="radio" aria-checked={kind === 'bulk'} className={kind === 'bulk' ? 'active' : ''} onClick={() => setKind('bulk')}>
              <span className="sk-ico"><Users size={18} /></span>
              <span><strong>Bulk email</strong><small>The same email to a list. Each person gets their own private copy, personalised with their name.</small></span>
            </button>
          </div>

          <div className="field">
            <label>From name <span className="muted">(optional)</span></label>
            <input placeholder="e.g. Hasan from Acme" value={fromName}
                   onChange={e => setFromName(e.target.value)} style={{ maxWidth: 320, marginBottom: 10 }} />
            <label>From address</label>
            <div className="from-row">
              <input className="from-local" placeholder="hello" value={local}
                     onChange={e => setLocal(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''))} />
              <span className="from-at">@</span>
              <div className="from-domain">
                <SearchableSelect value={activeDomain} onChange={setDomain}
                  options={sendable.map(d => ({ value: d.name, label: d.name }))}
                  placeholder="Select domain" searchPlaceholder="Search domains…" />
              </div>
            </div>
            <div className="hint">
              Sending as <span className="mono">{from || '…'}</span>.
              {sendable.length > 1
                ? ' Pick which verified domain to send from on the right.'
                : ' This is your only verified sending domain.'}
            </div>
          </div>

          {kind === 'bulk' && (
            <div className="field">
              <div className="spread" style={{ alignItems: 'center', marginBottom: 4 }}>
                <label htmlFor="bulk-to" style={{ margin: 0 }}>Recipients</label>
                <span className="row" style={{ gap: 10 }}>
                  <input ref={csvInput} type="file" accept=".csv,.txt,text/csv,text/plain" hidden
                         onChange={e => { loadCsv(e.target.files?.[0]); e.target.value = '' }} />
                  <button type="button" className="linklike" style={{ fontSize: 12.5 }} onClick={() => csvInput.current?.click()}>Upload CSV</button>
                  {bulkRaw && <button type="button" className="linklike" style={{ fontSize: 12.5 }} onClick={() => setBulkRaw('')}>Clear</button>}
                </span>
              </div>
              <textarea id="bulk-to" rows={7} className="mono" value={bulkRaw} onChange={e => setBulkRaw(e.target.value)}
                        placeholder={'ali@example.com, Ali Khan\nsara@example.co.uk, Sara Smith\nJohn Doe <john@example.de>'} />
              <div className="hint">One per line: <span className="mono">email, name</span> — or upload a CSV with an email column (and optionally a name column).</div>
              {bulkRaw.trim() && (
                <div className="bulk-stats">
                  <span className="badge ok">{parsed.list.length} recipient{parsed.list.length === 1 ? '' : 's'}</span>
                  {parsed.duplicates > 0 && <span className="badge neutral">{parsed.duplicates} duplicate{parsed.duplicates === 1 ? '' : 's'} removed</span>}
                  {parsed.invalid.length > 0 && <span className="badge failed">{parsed.invalid.length} line{parsed.invalid.length === 1 ? '' : 's'} skipped</span>}
                </div>
              )}
              {parsed.invalid.length > 0 && (
                <div className="bulk-invalid">
                  No valid email on: {parsed.invalid.slice(0, 3).map((l, i) => <code key={i}>{l.length > 40 ? l.slice(0, 40) + '…' : l}</code>)}
                  {parsed.invalid.length > 3 && <>and {parsed.invalid.length - 3} more</>}
                </div>
              )}
              <div className="hint">
                Personalise with <span className="mono">{'{{name}}'}</span>, <span className="mono">{'{{first_name}}'}</span> and <span className="mono">{'{{email}}'}</span> in the subject or body
                (an empty name becomes “there”). Unsubscribed and bounced addresses are skipped automatically.
              </div>
            </div>
          )}

          {kind === 'single' && <div className="field">
            <div className="spread" style={{ alignItems: 'center', marginBottom: 4 }}>
              <label htmlFor="to" style={{ margin: 0 }}>To</label>
              {showCcBcc
                ? (
                  <button type="button" className="linklike" style={{ fontSize: 12.5 }}
                          onClick={() => { setShowCcBcc(false); setCc(''); setBcc('') }}>
                    − Hide Cc / Bcc
                  </button>
                )
                : (
                  <button type="button" className="linklike" style={{ fontSize: 12.5 }}
                          onClick={() => setShowCcBcc(true)}>+ Cc / Bcc</button>
                )}
            </div>
            <input id="to" placeholder="someone@example.com" value={to}
                   onChange={e => setTo(e.target.value)} />
            <div className="hint">Separate multiple recipients with a comma.</div>
            {showCcBcc && (
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                <div>
                  <label htmlFor="cc" className="small">Cc</label>
                  <input id="cc" placeholder="cc@example.com, …" value={cc} onChange={e => setCc(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="bcc" className="small">Bcc <span className="muted">(hidden from other recipients)</span></label>
                  <input id="bcc" placeholder="bcc@example.com, …" value={bcc} onChange={e => setBcc(e.target.value)} />
                </div>
              </div>
            )}
          </div>}

          {templates.length > 0 && (
            <div className="field">
              <label>Template <span className="muted">(optional)</span></label>
              <SearchableSelect value={templateId} onChange={pickTemplate}
                options={[{ value: '', label: '— No template —' },
                  ...templates.map((t: any) => ({ value: t.id, label: t.name, sub: t.slug }))]}
                placeholder="Choose a template" searchPlaceholder="Search templates…" />
              <div className="hint">Loads the template’s subject and body — you can still edit before sending.</div>
            </div>
          )}

          <div className="field">
            <label htmlFor="su">Subject</label>
            <input id="su" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>

          {placeholders.length > 0 && (
            <div className="field">
              <label>Template variables</label>
              <div className="hint" style={{ marginTop: 0, marginBottom: 8 }}>
                Your subject/body use <span className="mono">{'{{…}}'}</span> placeholders — fill their values here. They’re substituted before sending.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
                {placeholders.map(k => (
                  <div key={k}>
                    <label className="small mono" style={{ display: 'block', marginBottom: 4 }}>{`{{${k}}}`}</label>
                    <input value={vars[k] ?? ''} placeholder={`value for ${k}`}
                           onChange={e => setVars(v => ({ ...v, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="field">
            <div className="spread" style={{ alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
              <label htmlFor="bd" style={{ margin: 0 }}>{mode === 'html' ? 'HTML body' : 'Message'}</label>
              <div className="row" style={{ gap: 8 }}>
                {mode === 'html' && (
                  <div className="segmented">
                    <button type="button" className={!source ? 'active' : ''} onClick={() => setSource(false)}>Design</button>
                    <button type="button" className={source ? 'active' : ''} onClick={() => setSource(true)}>Source</button>
                  </div>
                )}
                <div className="segmented">
                  <button type="button" className={mode === 'text' ? 'active' : ''} onClick={() => setMode('text')}>Text</button>
                  <button type="button" className={mode === 'html' ? 'active' : ''} onClick={() => setMode('html')}>HTML</button>
                </div>
              </div>
            </div>
            {mode === 'html' && !source
              ? <RichText value={text} onChange={setText} />
              : <textarea id="bd" rows={mode === 'html' ? 10 : 6} value={text}
                          onChange={e => setText(e.target.value)}
                          className={mode === 'html' ? 'mono' : ''}
                          placeholder={mode === 'html' ? '<h1>Hi</h1><p>…</p>' : ''} />}
            <div className="hint">
              {mode === 'html'
                ? (source ? 'Raw HTML source.' : 'Visual editor — use the toolbar to format. Open/click tracking is added automatically.')
                : 'Plain text.'}
            </div>
          </div>

          {kind === 'single' && <div className="field">
            <label>Attachments <span className="muted">(optional)</span></label>
            <input ref={fileInput} type="file" multiple hidden
                   onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
            <div className={`dropzone ${dragOver ? 'over' : ''}`}
                 role="button" tabIndex={0}
                 onClick={() => fileInput.current?.click()}
                 onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click() }}
                 onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                 onDragLeave={() => setDragOver(false)}
                 onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}>
              <span className="dz-ico"><UploadCloud size={22} /></span>
              <div className="dz-text"><strong>Click to upload</strong> or drag &amp; drop</div>
              <div className="dz-sub">Word, PDF, images, txt — up to 10 MB total</div>
            </div>
            {files.length > 0 && (
              <div className="file-grid">
                {files.map((f, i) => {
                  const isImg = f.type.startsWith('image/')
                  return (
                    <div key={i} className="file-card">
                      {isImg
                        ? <img className="file-thumb" src={URL.createObjectURL(f)} alt="" />
                        : <span className="file-ico">{fileGlyph(f)}</span>}
                      <div className="file-meta">
                        <span className="file-name" title={f.name}>{f.name}</span>
                        <span className="file-size">{fmtBytes(f.size)}</span>
                      </div>
                      <button type="button" className="file-x" title="Remove"
                              onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={14} /></button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>}

          {kind === 'bulk' && parsed.list[0] && (
            <div className="bulk-preview" aria-live="polite">
              <div className="bp-head">Preview · as {parsed.list[0].email} will see it</div>
              <div className="bp-subj">{personalize(fill(subject), parsed.list[0], false) || '(no subject)'}</div>
              <div className="bp-body">{personalize(fill(mode === 'html' ? text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : text), parsed.list[0], false)}</div>
            </div>
          )}

          {kind === 'bulk' && (
            <div className="field">
              <label className="row" style={{ gap: 8, fontWeight: 500 }}>
                <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} style={{ width: 'auto' }} />
                This is marketing (newsletter, offer, announcement)
              </label>
              <div className="hint" style={{ marginLeft: 24 }}>
                {marketing
                  ? <>Counts toward your daily marketing allowance. An unsubscribe link is added automatically — or place <span className="mono">{'{{unsubscribe_url}}'}</span> where you want it.</>
                  : 'Counts toward your daily transactional allowance. Only use this for service messages people expect (account notices, order updates).'}
              </div>
              {marketing && (
                <label className="row" style={{ gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ width: 'auto', marginTop: 3 }} />
                  <span className="small" style={{ fontWeight: 400 }}>These people agreed to receive email from me (opt-in or an existing customer relationship). No bought or scraped lists — see the <a href="https://itsbingmybusiness.com/legal/acceptable-use" target="_blank" rel="noreferrer">Acceptable Use Policy</a>.</span>
                </label>
              )}
              <div className="hint">Attachments aren’t available for bulk email — link to files instead. For lists you mail regularly, use <Link to="/campaigns">Campaigns</Link> (saved audiences, scheduling, reports).</div>
            </div>
          )}

          {sandbox && (
            <div className="alert info" style={{ marginBottom: 14 }}>
              <div>
                <strong>Sandbox mode.</strong> You can only send to addresses verified on
                this account until it is approved.
              </div>
            </div>
          )}

          {kind === 'single' ? (
            <button disabled={send.isPending} onClick={trySend}>
              {send.isPending ? 'Sending…' : 'Send message'}
            </button>
          ) : (
            <div className="allowance">
              <button disabled={bulkSend.isPending || parsed.list.length === 0 || overAllowance} onClick={tryBulk}>
                {bulkSend.isPending && progress
                  ? `Sending… ${progress.done} of ${progress.total}`
                  : `Send to ${parsed.list.length} recipient${parsed.list.length === 1 ? '' : 's'}`}
              </button>
              {marketing && marketingLeft != null && (
                <span className={`meter ${overAllowance ? 'over' : ''}`}>
                  {overAllowance
                    ? <>Only {marketingLeft} marketing emails left today. <Link to="/limits">Change your split</Link></>
                    : <>{marketingLeft} of {limits?.marketing_daily} marketing emails left today</>}
                </span>
              )}
            </div>
          )}

          {kind === 'bulk' && bulkResult && (
            <div className={`alert ${bulkResult.queued > 0 ? 'info' : 'error'} reveal`} style={{ marginTop: 14, marginBottom: 0 }}>
              <div>
                <strong>{bulkResult.queued} queued</strong>{bulkResult.failed > 0 && <>, {bulkResult.failed} not sent</>}.
                {' '}<Link to="/messages">Open the message log</Link> to follow delivery.
                {bulkResult.errors.length > 0 && <ul className="small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>{bulkResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
              </div>
            </div>
          )}

          {kind === 'single' && error && <div className="alert error" style={{ marginTop: 14, marginBottom: 0 }}>{error.message}</div>}

          {kind === 'single' && result && (
            <div className="alert info reveal" style={{ marginTop: 14, marginBottom: 0 }}>
              <div>
                <strong>Accepted for delivery.</strong> Queued to {result.recipients} recipient
                {result.recipients === 1 ? '' : 's'}.{' '}
                <Link to={`/messages/${result.id}`}>Track it</Link> — delivery events arrive
                as receivers respond.
              </div>
            </div>
          )}
        </div>
      )}

      <h2>Prefer the API?</h2>
      <pre className="code"><code>{`curl -X POST https://api.itsbingmybusiness.com/v1/messages \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "${from || 'hello@yourdomain.com'}",
    "to": ["someone@example.com"],
    "subject": "Hello",
    "text": "Sent through the ItsBingMyBusiness API."
  }'`}</code></pre>
    </div>
  )
}
